import Request from "../../../services/request";

/**
 * Подключения к номенклатуре узла схемы — условия по характеристикам
 * (характеристика/операнд/число) вместо привязки к конкретному товару
 * или группе товаров. Бэк: m_to_m_scheme_node_nomenclature_rules,
 * App\Http\Controllers\MToMSchemeNodeNomenclatureRuleController (agregatorus).
 *
 * Строка условия синхронизируется с бэком только когда полностью заполнена
 * (характеристика + операнд + число) — до этого момента это локальный
 * черновик (id === null), чтобы можно было добавить пустую строку и
 * заполнять её по ходу, как и раньше на прототипе с localStorage.
 *
 * rules — ВСЕ условия узла разом, плоским списком: у каждого — элемент узла
 * (id_scheme_node_element: кран, клапан и т.д.) и номер его экземпляра на SVG
 * (element_instance: КШ-1 → 1, КШ-2 → 2). Группировка по экземплярам (строки
 * таблицы «Оборудование») — забота компонента, не стора: один GET на диалог узла.
 */

const RULES_API = "/dictionary/scheme-node-nomenclature-rules";
const CHARACTERISTICS_API = "/dictionary/characteristic/nomenclature";
const DICTIONARY_ATTRIBUTES_API = "/dictionary/dictionary-attributes";

// Характеристики типа «Выбор из справочника» (d_type_characteristics.code) —
// значение сравнивается только на равенство, список значений берётся из
// самого справочника (d_dictionary_attributes), а не из каталога товаров.
export const DICTIONARY_TYPE_CODE = "vybor-iz-spravocnika";

export const OPERANDS = [
  { value: "eq", label: "=" },
  { value: "neq", label: "≠" },
  { value: "gt", label: ">" },
  { value: "gte", label: "≥" },
  { value: "lt", label: "<" },
  { value: "lte", label: "≤" },
];
export const DEFAULT_OPERAND = "eq";

let draftSeq = 1;
function nextDraftUid() {
  return `draft${Date.now().toString(36)}_${draftSeq++}`;
}

function isComplete(row) {
  // value — число для числовых характеристик, но может быть и текстом
  // значения справочника ("Нержавеющая сталь") — проверяем только на
  // непустоту, не на Number(), иначе текстовые условия никогда не уйдут на бэк.
  return (
    row.id_characteristic !== null &&
    row.id_characteristic !== undefined &&
    !!row.operand &&
    row.value !== null &&
    row.value !== "" &&
    row.value !== undefined
  );
}

function toRow(serverRow) {
  return {
    uid: `id${serverRow.id}`,
    id: serverRow.id,
    id_scheme_node_element: serverRow.id_scheme_node_element ?? null,
    element_instance: serverRow.element_instance ?? null,
    // название/обозначение элемента — для условий экземпляра, которого на SVG уже нет
    element_name: serverRow._element ? serverRow._element.name_ru : null,
    element_marking: serverRow._element ? serverRow._element.marking_ru : null,
    id_characteristic: serverRow.id_characteristic,
    operand: serverRow.operand,
    value: serverRow.value,
  };
}

export default {
  namespaced: true,
  state: {
    idSchemeNode: null,
    rules: [],
    characteristics: [],
    loading: false,
    characteristicsLoading: false,
    // Подбор по кнопке «Показать подходящую номенклатуру» — по СУММАРНЫМ
    // условиям узла (всего узла + всех его элементов разом), не автозагружается,
    // и сбрасывается при любом изменении условий, чтобы не показывать устаревшее.
    candidates: [],
    candidatesLoaded: false,
    // причина пустого результата от бэка (нет семейства, разные типы оборудования…)
    candidatesMessage: "",
    candidatesLoading: false,
    // Значения характеристики, реально встречающиеся в каталоге — подсказка
    // в поле «Число» (v-combobox). Кэш по id_characteristic, грузится лениво
    // по фокусу на поле, не для всех строк сразу.
    valuesByCharacteristic: {},
    valuesLoadingByCharacteristic: {},
    // Для характеристик типа «Выбор из справочника» — значения берутся из
    // самого справочника (d_dictionary_attributes), кэш по id_dictionary.
    dictionaryValuesById: {},
    dictionaryValuesLoadingById: {},
  },
  getters: {
    characteristicOptions(state) {
      return [...state.characteristics]
        .sort((a, b) => a.name.localeCompare(b.name, "ru"))
        .map((c) => ({
          id: c.id,
          label: c.postfix ? `${c.name}, ${c.postfix}` : c.name,
        }));
    },
    characteristicById: (state) => (id) => state.characteristics.find((c) => c.id === id),
  },
  mutations: {
    setIdSchemeNode(state, id) {
      state.idSchemeNode = id || null;
    },
    setRules(state, rows) {
      state.rules = (Array.isArray(rows) ? rows : []).map(toRow);
    },
    // idSchemeNodeElement и instance — к какому экземпляру элемента (КШ-2 → элемент
    // «Кран шаровый», instance 2) относится черновик: строка, в которой нажали «Добавить условие».
    addDraftRow(state, { idSchemeNodeElement, instance }) {
      state.rules = [
        ...state.rules,
        {
          uid: nextDraftUid(),
          id: null,
          id_scheme_node_element: idSchemeNodeElement || null,
          element_instance: instance || null,
          id_characteristic: null,
          operand: DEFAULT_OPERAND,
          value: null,
        },
      ];
    },
    patchRow(state, { uid, patch }) {
      state.rules = state.rules.map((row) =>
        row.uid === uid ? { ...row, ...patch } : row
      );
    },
    replaceRow(state, { uid, serverRow }) {
      state.rules = state.rules.map((row) =>
        row.uid === uid ? toRow(serverRow) : row
      );
    },
    removeRow(state, uid) {
      state.rules = state.rules.filter((row) => row.uid !== uid);
    },
    setCharacteristics(state, list) {
      state.characteristics = Array.isArray(list) ? list : [];
    },
    changeLoading(state, value) {
      state.loading = value;
    },
    changeCharacteristicsLoading(state, value) {
      state.characteristicsLoading = value;
    },
    setCandidates(state, { items, message }) {
      state.candidates = Array.isArray(items) ? items : [];
      state.candidatesMessage = state.candidates.length ? "" : message || "";
      state.candidatesLoaded = true;
    },
    resetCandidates(state) {
      state.candidates = [];
      state.candidatesLoaded = false;
    },
    changeCandidatesLoading(state, value) {
      state.candidatesLoading = value;
    },
    setCharacteristicValues(state, { idCharacteristic, values }) {
      state.valuesByCharacteristic = {
        ...state.valuesByCharacteristic,
        [idCharacteristic]: values,
      };
    },
    setCharacteristicValuesLoading(state, { idCharacteristic, loading }) {
      state.valuesLoadingByCharacteristic = {
        ...state.valuesLoadingByCharacteristic,
        [idCharacteristic]: loading,
      };
    },
    setDictionaryValues(state, { idDictionary, values }) {
      state.dictionaryValuesById = {
        ...state.dictionaryValuesById,
        [idDictionary]: values,
      };
    },
    setDictionaryValuesLoading(state, { idDictionary, loading }) {
      state.dictionaryValuesLoadingById = {
        ...state.dictionaryValuesLoadingById,
        [idDictionary]: loading,
      };
    },
  },
  actions: {
    // Каталог характеристик общий для всех узлов — грузим один раз и кэшируем.
    async fetchCharacteristics({ state, commit }) {
      if (state.characteristics.length || state.characteristicsLoading) return;
      commit("changeCharacteristicsLoading", true);
      try {
        const query = Request.modifyQuery({}, [
          "id",
          "name",
          "postfix",
          "type_characteristic",
          "id_dictionary",
        ]);
        const response = await Request.get(
          this.state.BASE_URL + `${CHARACTERISTICS_API}${query}`
        );
        if (response && !response.isError) {
          commit("setCharacteristics", response.data);
        }
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
      } finally {
        commit("changeCharacteristicsLoading", false);
      }
    },
    // Реальные значения характеристики из каталога — подсказка в «Число»
    // (v-combobox). Лениво по фокусу на поле, кэшируется на весь диалог.
    async fetchCharacteristicValues({ state, commit }, idCharacteristic) {
      if (
        !idCharacteristic ||
        state.valuesByCharacteristic[idCharacteristic] ||
        state.valuesLoadingByCharacteristic[idCharacteristic]
      ) {
        return;
      }
      commit("setCharacteristicValuesLoading", { idCharacteristic, loading: true });
      try {
        const response = await Request.get(
          this.state.BASE_URL +
            `${RULES_API}/characteristic-values?id_characteristic=${idCharacteristic}`
        );
        if (response && !response.isError) {
          commit("setCharacteristicValues", {
            idCharacteristic,
            values: response.data ? response.data.values : [],
          });
        }
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
      } finally {
        commit("setCharacteristicValuesLoading", { idCharacteristic, loading: false });
      }
    },
    // Значения справочника для характеристик типа «Выбор из справочника» —
    // канонический список (d_dictionary_attributes), не то, что реально
    // используется в каталоге. Лениво по фокусу, кэшируется на весь диалог.
    async fetchDictionaryValues({ state, commit }, idDictionary) {
      if (
        !idDictionary ||
        state.dictionaryValuesById[idDictionary] ||
        state.dictionaryValuesLoadingById[idDictionary]
      ) {
        return;
      }
      commit("setDictionaryValuesLoading", { idDictionary, loading: true });
      try {
        const response = await Request.get(
          this.state.BASE_URL +
            `${DICTIONARY_ATTRIBUTES_API}?filter[id_dictionary]=${idDictionary}`
        );
        if (response && !response.isError) {
          commit("setDictionaryValues", {
            idDictionary,
            values: (response.data || []).map((a) => a.value),
          });
        }
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
      } finally {
        commit("setDictionaryValuesLoading", { idDictionary, loading: false });
      }
    },
    // Один запрос — сразу все условия узла (и его самого, и всех элементов).
    async fetchRules({ commit }, idSchemeNode) {
      commit("setIdSchemeNode", idSchemeNode);
      commit("resetCandidates");
      // условия прежнего узла не должны мелькать (и попадать в строки оборудования), пока
      // грузятся условия нового
      commit("setRules", []);
      if (!idSchemeNode) {
        commit("setRules", []);
        return;
      }
      commit("changeLoading", true);
      try {
        // не filter[]-обёртка: контроллер читает id_scheme_node напрямую
        const response = await Request.get(
          this.state.BASE_URL + `${RULES_API}?id_scheme_node=${idSchemeNode}`
        );
        if (response && !response.isError) commit("setRules", response.data);
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
      } finally {
        commit("changeLoading", false);
      }
    },
    clearRules({ commit }) {
      commit("setIdSchemeNode", null);
      commit("setRules", []);
      commit("resetCandidates");
    },
    addDraftRule({ commit }, { idSchemeNodeElement = null, instance = null } = {}) {
      commit("addDraftRow", { idSchemeNodeElement, instance });
    },
    async updateRule({ state, commit, dispatch }, { uid, key, value }) {
      commit("patchRow", { uid, patch: { [key]: value } });

      const row = state.rules.find((r) => r.uid === uid);
      if (!row || !isComplete(row)) return;

      if (row.id) {
        await dispatch("persistUpdate", row);
      } else {
        await dispatch("persistCreate", row);
      }
    },
    async persistCreate({ state, commit }, row) {
      commit("changeLoading", true);
      try {
        const response = await Request.post(this.state.BASE_URL + RULES_API, {
          id_scheme_node: state.idSchemeNode,
          id_scheme_node_element: row.id_scheme_node_element,
          element_instance: row.element_instance,
          id_characteristic: row.id_characteristic,
          operand: row.operand,
          value: row.value,
        });
        if (response && !response.isError && response.data) {
          commit("replaceRow", { uid: row.uid, serverRow: response.data });
          commit("resetCandidates");
        } else if (response && response.isError) {
          commit("change_notification_modal", response, { root: true });
        }
        return response;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
      } finally {
        commit("changeLoading", false);
      }
    },
    async persistUpdate({ commit }, row) {
      commit("changeLoading", true);
      try {
        const response = await Request.put(
          this.state.BASE_URL + `${RULES_API}/${row.id}`,
          {
            id_characteristic: row.id_characteristic,
            operand: row.operand,
            value: row.value,
          }
        );
        if (response && !response.isError) {
          commit("resetCandidates");
        } else if (response && response.isError) {
          commit("change_notification_modal", response, { root: true });
        }
        return response;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
      } finally {
        commit("changeLoading", false);
      }
    },
    async removeRule({ state, commit }, uid) {
      const row = state.rules.find((r) => r.uid === uid);
      if (!row) return;

      // черновик без id ещё не уходил на бэк — просто убираем локально
      if (!row.id) {
        commit("removeRow", uid);
        commit("resetCandidates");
        return;
      }

      commit("changeLoading", true);
      try {
        const response = await Request.delete(
          this.state.BASE_URL + `${RULES_API}/${row.id}`
        );
        if (response && !response.isError) {
          commit("removeRow", uid);
          commit("resetCandidates");
        }
        return response;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
      } finally {
        commit("changeLoading", false);
      }
    },
    // По кнопке «Показать подходящую номенклатуру» — весь каталог,
    // отфильтрованный по сумме условий переданных экземпляров оборудования
    // (строки с чекбоксом в таблице «Оборудование»): instances — строки вида
    // «id_элемента:номер» (1:2 → КШ-2); пустой список — по всем условиям узла
    // разом. Не вызывается автоматически.
    async fetchCandidates({ state, commit }, instances = []) {
      if (!state.idSchemeNode) return;
      commit("changeCandidatesLoading", true);
      try {
        let url = `${RULES_API}/candidates?id_scheme_node=${state.idSchemeNode}`;
        instances.forEach((pair) => {
          url += `&instances[]=${encodeURIComponent(pair)}`;
        });
        const response = await Request.get(this.state.BASE_URL + url);
        if (response && !response.isError) {
          commit("setCandidates", {
            items: response.data ? response.data.items : [],
            message: response.data && response.data.problems ? response.data.problems.map((p) => p.message).join("; ") : "",
          });
        } else if (response && response.isError) {
          commit("change_notification_modal", response, { root: true });
        }
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
      } finally {
        commit("changeCandidatesLoading", false);
      }
    },
  },
};
